/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Member attendance Firebase Callable Function Wrappers (coach portal)
 */

import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "../store/firebaseApp";
import { Timestamp } from "firebase/firestore";
import type {
  SetMemberPriorClassesRequest,
  SetMemberPriorClassesResponse,
} from "@lemonadetraining/contracts";

/**
 * Get member analytics by specific UID (optimized for single member lookup)
 * Performance: Direct document read by UID instead of querying all members
 */
export const getMemberAnalyticsByUID = async (data: {
  boxID: string;
  memberUID: string;
}): Promise<{
  member: {
    athleteUID: string;
    totalBookings: number;
    checkedInBookings: number;
    cancelledBookings: number;
    noShowBookings: number;
    checkInRate: number;
    lastBookingDate: Timestamp | null;
    lastCheckInDate: Timestamp | null;
    daysSinceLastBooking: number | null;
    daysSinceLastCheckIn: number | null;
    engagementLevel: "high" | "medium" | "low";
    isActive: boolean;
    // Optional on the wire: hosting and functions do not deploy atomically, so
    // a client can receive a response from a backend that predates this field.
    hasBooked?: boolean;
    favoriteClassTypes: string[];
    classTypeBreakdown: { [key: string]: { bookings: number; checkIns: number } };
    visitCardsUsed: number;
    visitCardsRemaining: number;
    averageBookingsPerWeek: number;
    bookingsInPeriod: number;
    checkInsInPeriod: number;
    priorClasses: number;
    checkedInLive: number;
  } | null;
}> => {
  const getMemberAnalyticsByUIDFn = httpsCallable(firebaseFunctions, "getMemberAnalyticsByUID");
  const result = await getMemberAnalyticsByUIDFn(data);
  return result.data as any;
};

/**
 * Set a member's pre-Lemonade class count (admin-only, box-scoped, SET semantics).
 *
 * Typed from the published contract rather than an inline shape: nothing in this
 * repo typechecks the wire, so a restated shape can drift from the callable
 * without any compiler noticing.
 */
export const setMemberPriorClasses = async (
  data: SetMemberPriorClassesRequest
): Promise<SetMemberPriorClassesResponse> => {
  const setMemberPriorClassesFn = httpsCallable<
    SetMemberPriorClassesRequest,
    SetMemberPriorClassesResponse
  >(firebaseFunctions, "setMemberPriorClasses");
  const result = await setMemberPriorClassesFn(data);
  return result.data;
};
